/* eslint-disable no-secrets/no-secrets */
/* eslint-disable pii/no-phone-number */
import { AddToCartContext, AddToCartEvent, OriginAddToCart } from './events/add-to-cart.model';
import { ImpressionsHitContext, ImpressionsHitEvent } from './events/impressions-hit.model';
import { OriginPageHit, PageHitContext, PageHitEvent } from './events/page-hit-event.model';
import { PurchaseConfirmedContext, PurchaseConfirmedEvent } from './events/purchase-confirmed.model';

import { CartItem } from '@api/cart/cart.models';
import { Item } from '@api/catalog/items/medias/items.models';
import { Product } from '@api/catalog/products/products.models';
import { CurrencyInfo } from '@core/domain/models/store.models';
import { EmpathySearcherOrigin } from '@core/domain/types/empathy-searcher-origin.type';
import { PageSection } from '@core/domain/types/page-sections.types';
import { QueryResult, SearchType } from '@features/search/domain/models/search.models';
import { EventFrom } from '@shared/analytics/COMMON/models/analytics-common.models';
import {
    SearchAssistantContext,
    SearchAssistantEvent,
} from '@shared/analytics/ZENIT/models/events/search-assistant.model';
import { RelatedsPlace } from '@shared/product/related-products/application/store/related-products.state';
import { ProductViewPlace, RelatedsProvider } from '@shared/product/related-products/domain/models/related-products.models';
import { GridType } from '@shared/ui/models/grid-type.model';
import { ModalData } from '@ui/dialog/store/modal.state';

export enum ZenitEventName {
    AddToCart = 'AddToCart',
    ImpressionsHit = 'ImpressionsHit',
    PageHit = 'PageHit',
    PurchaseConfirmed = 'PurchaseConfirmed',
    SearchAssistant = 'SearchAssistant',
}

export type ZenitEvent = AddToCartEvent | PageHitEvent | PurchaseConfirmedEvent | ImpressionsHitEvent | SearchAssistantEvent;
export interface ZenitEventValues {
    brandId?: 16;
    cartId?: string;
    cartItems?: CartItem[];
    categoryId?: string;
    categoryProductId?: string;
    categoryUrl?: string;
    currency?: CurrencyInfo;
    displayLanguage?: string;
    filteredProductsIds?: number[];
    isSelectedAddress?: boolean;
    item?: CartItem | Product;
    items?: CartItem[] | Item[] | Product[];
    modalData?: ModalData<any>;
    originItemListIndex?: number;
    originItemListName?: ZenitList;
    originItemRelatedType?: EventFrom;
    page?: PageSection;
    productCategoryUrl?: string;
    productPageProduct?: Product;
    queryResults?: QueryResult[];
    relatedProductsPlaces?: Record<ProductViewPlace, RelatedsPlace>;
    section?: string;
    selectedViewPickup?: 'list' | 'map';
    sourceEventFromItem?: EventFrom;
    storeId?: string;
    wishListItems?: Product[];
    zoomLevel?: GridType;
    searchRequestQuery?: string;
    searchRequestFilters?: string;
    searchRequestOffset?: number;
}

export interface ZenitEventData {
    items?: CartItem[] | Item[] | Product[];
    origin?: EmpathySearcherOrigin | SearchType;
    place?: ProductViewPlace;
    relatedsSectionId?: string;
    searchTerm?: string;
    totalHits?: string | number;
    responseTime?: number;
    totalResults?: number;
    responseCode?: number;
    searchPass?: string;
    provider?: string;
}

// Parámetros existentes de los eventos de Zenit
export type ZenitEventParam =
    keyof AddToCartContext
    | keyof ImpressionsHitContext
    | keyof PageHitContext
    | keyof PurchaseConfirmedContext
    | keyof SearchAssistantContext;

// Forma de crear un "Enum" a partir de otros enums sin tener que declarar todas las propiedades de nuevo
export const ZenitEventOrigin = {
    ...OriginAddToCart,
    ...OriginPageHit,
} as const;

// TODO ECOMDUTI-22445 Locate massimo dutti page types
export enum ZenitPageType {
    BUY_AGAIN = 'BUY_AGAIN',
    CATEGORY_VIEW = 'CATEGORY_VIEW',
    CHECKOUT_ADD_PERSONAL_DETAILS = 'CHECKOUT_ADD_PERSONAL_DETAILS',
    CHECKOUT_DROP_POINT_DETAIL = 'CHECKOUT_DROP_POINT_DETAIL',
    CHECKOUT_DROP_POINT_LIST = 'CHECKOUT_DROP_POINT_LIST',
    CHECKOUT_DROP_POINT_MAP = 'CHECKOUT_DROP_POINT_MAP',
    CHECKOUT_HOME_ADDRESS_LIST = 'CHECKOUT_HOME_ADDRESS_LIST',
    CHECKOUT_MODIFY_PERSONAL_DETAILS = 'CHECKOUT_MODIFY_PERSONAL_DETAILS',
    CHECKOUT_ORDER_REVIEW = 'CHECKOUT_ORDER_REVIEW',
    CHECKOUT_PAYMENT_METHOD = 'CHECKOUT_PAYMENT_METHOD',
    CHECKOUT_REPAYMENT = 'CHECKOUT_REPAYMENT',
    CHECKOUT_SHIPPING_METHOD = 'CHECKOUT_SHIPPING_METHOD',
    CHECKOUT_STORE_COLLECTION_POINT_MAP = 'CHECKOUT_STORE_COLLECTION_POINT_MAP',
    CHECKOUT_STORE_DETAIL = 'CHECKOUT_STORE_DETAIL',
    CHECKOUT_STORE_LIST = 'CHECKOUT_STORE_LIST',
    CHECKOUT_TYPE_DELIVERY_METHOD = 'CHECKOUT_TYPE_DELIVERY_METHOD',
    E_GIFTCARD = 'E-GIFTCARD',
    GIFT_OPTIONS = 'GIFT_OPTIONS',
    GIFTCARD = 'GIFTCARD',
    HOME = 'HOME',
    LOOKBOOK = 'LOOKBOOK',
    MIX_MATCH_DETAIL = 'MIX_MATCH_DETAIL',
    MIX_MATCH_LIST = 'MIX_MATCH_LIST',
    PRODUCT_BUNDLE_DETAILS = 'PRODUCT_BUNDLE_DETAILS',
    PRODUCT_DETAILS = 'PRODUCT_DETAILS',
    PRODUCT_LIST = 'PRODUCT_LIST',
    PRODUCT_SEARCH_RESULTS = 'PRODUCT_SEARCH_RESULTS',
    PRODUCT_SEARCH_START = 'PRODUCT_SEARCH_START',
    PRODUCT_SET_DETAILS = 'PRODUCT_SET_DETAILS',
    PURCHASE_ORDER_END = 'PURCHASE_ORDER_END',
    SAVE_FOR_LATER = 'SAVE_FOR_LATER',
    SEARCH_REQUEST = 'SEARCH_REQUEST',
    SHOPPING_CART = 'SHOPPING_CART',
    SHOPPING_CART_EMPTY = 'SHOPPING_CART_EMPTY',
    SURPRISE_ME = 'SURPRISE_ME',
    UNKNOWN = 'UNKNOWN',
    USER_LOGON = 'USER_LOGON',
    VIRTUAL_TRY_ON = 'VIRTUAL_TRY_ON',
    WISHLIST = 'WISHLIST',
    WORLDWIDE = 'WORLDWIDE',
}

export enum ZenitList {
    alternativeColours = 'alternative-colours',
    alternativeResults = 'alternative-results',
    bundle = 'bundle',
    buyAgain = 'buy-again',
    buyLater = 'buy-later',
    crossCustomPdp = 'cross-custom-pdp',
    crossCustomToast = 'cross-custom-toast',
    crossRecommendation = 'cross-recommendation',
    crossRecommendationPdp = 'cross-recommendation-pdp',
    crossSellingCart = 'cross-selling-cart',
    crossSellingPdp = 'cross-selling-pdp',
    crossSellingToast = 'cross-selling-toast',
    crossSimilarBackSoon = 'cross-similar-back-soon',
    crossSimilarComingSoon = 'cross-similar-coming-soon',
    crossSimilarPdp = 'cross-similar-pdp',
    crossSimilarSearchResults = 'cross-similar-search-results',
    crossViewedRecently = 'cross-viewed-recently',
    edited = 'edited',
    grid = 'grid',
    matchWith = 'match-with',
    mayAlsoLike = 'may-also-like',
    noList = 'no-list',
    noListUndefined = 'no-list-undefined',
    noResultsFallback = 'no-results-fallback',
    saveForLater = 'save-for-later',
    searchAlternativeColours = 'search-alternative-colours',
    searchAlternativeResults = 'search-alternative-results',
    searchCrossCustom = 'search-cross-custom',
    searchNoResultsFallback = 'search-no-results-fallback',
    searchResults = 'search-results',
    searchSimilarQuery = 'search-similar-query',
    searchSuggestedProduct = 'search-suggested-product',
    shoppingCart = 'shopping-cart',
    sizeList = 'size-list',
    sizeRecommender = 'size-recommender',
    stradishoppers = 'stradishoppers',
    suggestedProduct = 'suggested-product',
    suggestedSearchResults = 'suggested-search-results',
    wishlist = 'wishlist',
}

export enum Section {
    MEN = 'MAN',
    WOMEN = 'WOMAN',
    KID = 'KID',
    UNKNOWN = 'UNKNOWN',
}

export enum ZenitZoomLevel {
    ZOOM1 = 'ZOOM1',
    ZOOM2 = 'ZOOM2',
    ZOOM3 = 'ZOOM3',
}

export interface ZenitItem {
    /* Total amount charged for this product in local currency, store currency */
    amount: number;
    /* Code of the brand that is selling this product [Bershka: 4, Lefties: 8, MassimoDutti: 11, Oysho: 7, Pull&Bear: 2, Stradivarius: 6, Uterqüe: 18, Zara: 1, 16, ZaraHome: 14] */
    articleBrandCode?: number;
    /* Unitary price of the product */
    price: number;
    /* SKU including campaign. This is MOCACOTA. Example: 0807324080005-I2021 */
    sku: string;
    /* Number of units purchased */
    units: number;
    section?: Section;
    brandId?: number;
}

export type ItemTypes = CartItem | Product | Item;

export interface ZenitPersonalizationParam {
    /** Objective of the personalization strategy. Examples: othersyoumaylike-cvr-divnone */
    objective?: string;
    /** Name of the recomendation provider. Examples: Google, YODA */
    provider: string;
    /** Name of the method or strategy followed in the recommendation. Examples: FBT, OYML, Similars */
    strategy?: string;
    /** Version of the strategy. Examples: 1.0, 2.0 */
    version?: string;
    /** This value is optional. Contains extra information about the personalization that could be gathered. */
    extra?: Record<string, any>;
}

export interface ZenitClickOrigin {
    /** Original list from which the recommended product comes from */
    list: ZenitList;
    /** Name of the original recomendation provider. Examples: Google, YODA */
    provider: RelatedsProvider;
    /** Name of the original method or strategy followed in the recommendation. Examples: FBT, OYML, Similars */
    strategy: EventFrom;
}
